// Money / number / date formatting (architecture.md §2, §19).
// Amounts are stored in paise; divide by 100 for display. Default locale en-IN.

/**
 * Format integer minor units (paise) as a currency string.
 * @param {number} minor  integer minor units (e.g. 74900 = ₹749.00)
 * @param {string} [currency='INR']
 * @param {string} [locale='en-IN']
 * @returns {string}
 */
export function formatMoney(minor, currency = 'INR', locale = 'en-IN') {
  const amount = Number(minor) / 100;
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
}

/**
 * Format a number with locale grouping (en-IN uses lakh/crore grouping).
 * @param {number} value
 * @param {string} [locale='en-IN']
 */
export function formatNumber(value, locale = 'en-IN') {
  return new Intl.NumberFormat(locale).format(value);
}

/**
 * Format a UTC instant in a target timezone. Times are always rendered per recipient tz.
 * @param {string|Date} instant  ISO string or Date (UTC)
 * @param {string} timeZone      IANA tz, e.g. 'Asia/Kolkata'
 * @param {string} [locale='en-IN']
 * @param {Intl.DateTimeFormatOptions} [opts]
 */
export function formatDateTime(instant, timeZone, locale = 'en-IN', opts = {}) {
  const d = instant instanceof Date ? instant : new Date(instant);
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    dateStyle: 'medium',
    timeStyle: 'short',
    ...opts,
  }).format(d);
}

/**
 * DD/MM/YYYY date display (§2).
 * @param {string|Date} instant
 * @param {string} timeZone
 * @param {string} [locale='en-IN']
 */
export function formatDate(instant, timeZone, locale = 'en-IN') {
  const d = instant instanceof Date ? instant : new Date(instant);
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}

/**
 * Time-only display (HH:MM) in a target tz.
 */
export function formatTime(instant, timeZone, locale = 'en-IN') {
  const d = instant instanceof Date ? instant : new Date(instant);
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}
